import { afterAll, afterEach, beforeAll, describe, expect, it } from '@jest/globals';
import { createTempDir, removeTempDir } from '@ms-cloudpack/test-utilities';
import fs from 'fs';
import path from 'path';
import type { UserPreferences } from '@ms-cloudpack/common-types';
import { getUserPreferencesPath, setMockUserPreferencesDir } from './getUserPreferencesPath.js';
import { updateUserPreference } from './updateUserPreference.js';
import { readJsonSync, writeJsonSync } from '@ms-cloudpack/json-utilities';

describe('updateUserPreference', () => {
  let mockUserPreferencesDir: string;
  let mockUserPreferencesPath: string;

  beforeAll(() => {
    mockUserPreferencesDir = createTempDir();
    setMockUserPreferencesDir(mockUserPreferencesDir);
    mockUserPreferencesPath = getUserPreferencesPath();
  });

  afterEach(() => {
    // Delete any preferences after each run
    fs.rmSync(mockUserPreferencesPath, { force: true });
    // One test modifies this, so restore it
    setMockUserPreferencesDir(mockUserPreferencesDir);
  });

  afterAll(async () => {
    await removeTempDir(mockUserPreferencesDir);
  });

  it('throws if test tries to update preferences without setting a mock dir', async () => {
    setMockUserPreferencesDir('');
    await expect(() => updateUserPreference({ key: 'cachePath', newValue: 'test' })).rejects.toThrow(
      'Tests must call setMockUserPreferencesDir before calling updateUserPreference.',
    );
  });

  it('throws on an invalid key', async () => {
    await expect(() => updateUserPreference({ key: 'invalid' as unknown as keyof UserPreferences })).rejects.toThrow(
      '"invalid" is not a valid user preference name',
    );
  });

  it('throws on undefined new value', async () => {
    await expect(() => updateUserPreference({ key: 'cachePath' })).rejects.toThrow(
      'Must specify newValue or deleteValue (this is a bug in Cloudpack)',
    );
  });

  it('throws on empty cachePath', async () => {
    await expect(() => updateUserPreference({ key: 'cachePath', newValue: '' })).rejects.toThrow(
      'The cache folder cannot be empty.',
    );
  });

  it('throws on relative cachePath', async () => {
    await expect(() => updateUserPreference({ key: 'cachePath', newValue: 'relative/path' })).rejects.toThrow(
      'The cache folder must be an absolute path.',
    );

    // documenting: ~ is not expanded
    await expect(() => updateUserPreference({ key: 'cachePath', newValue: '~/path' })).rejects.toThrow(
      'The cache folder must be an absolute path.',
    );
  });

  it("throws if parent of cachePath doesn't exist", async () => {
    await expect(() =>
      updateUserPreference({ key: 'cachePath', newValue: path.join(process.cwd(), 'does/not/exist') }),
    ).rejects.toThrow(/The parent folder .* of the cache folder does not exist/);
  });

  it('sets a valid cachePath in a new preferences file', async () => {
    // reuse the mock preferences dir
    const cachePath = path.join(mockUserPreferencesDir, 'cache');
    await updateUserPreference({ key: 'cachePath', newValue: cachePath });

    expect(readJsonSync(mockUserPreferencesPath)).toEqual({
      $schema: expect.any(String),
      cachePath,
    });
  });

  it('updates the cachePath in an existing preferences file', async () => {
    // this can be an invalid value because it didn't go through the validation
    writeJsonSync(mockUserPreferencesPath, { cachePath: 'old-cache' });

    const cachePath = path.join(mockUserPreferencesDir, 'cache');
    await updateUserPreference({ key: 'cachePath', newValue: cachePath });

    expect(readJsonSync(mockUserPreferencesPath)).toEqual({
      $schema: expect.any(String),
      cachePath,
    });
  });

  it('deletes the cachePath but leaves the preferences file', async () => {
    writeJsonSync(mockUserPreferencesPath, { cachePath: 'old-cache' });

    await updateUserPreference({ key: 'cachePath', deleteValue: true });

    expect(readJsonSync(mockUserPreferencesPath)).toEqual({ $schema: expect.any(String) });
  });
});
